import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowRight } from 'lucide-react';
import { PARTNER_ORGS_FALLBACK } from '../data/workshops';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact partner logo strip designed to mount on the homepage, /assessment, etc.
 * Fetches live data from /api/case-studies with a static fallback.
 * Props:
 *   - variant: 'light' | 'dark' (background contrast)
 *   - limit: number of logos to show (default 10)
 *   - showCta: boolean — display "View all case studies" link
 *   - heading: optional custom heading
 *   - subheading: optional custom subheading
 */
/**
 * Single partner logo tile.
 * Falls back to a text-only badge if the CDN image fails to load
 * (handles intermittent CDN hiccups + protects above-the-fold social proof).
 */
function PartnerLogo({ org, isLight }) {
  const [errored, setErrored] = useState(false);

  // Slightly taller tile + soft shadow for premium feel against dark hero.
  const cls = `group relative h-24 sm:h-28 rounded-xl border ${
    isLight
      ? 'border-slate-200 bg-white hover:border-violet-400 hover:shadow-md'
      : 'border-white/10 bg-white hover:border-violet-500/40 shadow-sm hover:shadow-lg hover:shadow-violet-500/10'
  } transition-all duration-300 flex items-center justify-center px-5 py-4 sm:px-6 sm:py-5 overflow-hidden`;

  const inner = errored ? (
    <span
      className="text-xs sm:text-sm font-semibold text-slate-700 text-center leading-tight px-2"
      data-testid="partner-logo-fallback"
    >
      {org.sub || org.name}
    </span>
  ) : (
    <img
      src={org.logo}
      alt={`${org.sub || org.name} logo`}
      // Cap BOTH height and width so wide marks (NCA) and tall marks (UNLV)
      // present at similar visual weight — curated-look across the strip.
      className="max-h-14 sm:max-h-16 max-w-[78%] object-contain group-hover:scale-[1.04] transition-transform duration-300"
      decoding="async"
      onError={() => setErrored(true)}
      data-testid="partner-logo-img"
    />
  );

  return org.website ? (
    <a href={org.website} target="_blank" rel="noopener noreferrer" className={cls} title={org.sub}>
      {inner}
    </a>
  ) : (
    <div className={cls} title={org.sub}>
      {inner}
    </div>
  );
}

export function PartnerStrip({
  variant = 'dark',
  limit = 10,
  showCta = true,
  heading = 'Trusted by Organizations Across Nevada',
  subheading = 'School districts, federal agencies, universities, chambers, and workforce partners.',
}) {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/case-studies`, { params: { limit: 50 } })
      .then(({ data }) => {
        if (!mounted) return;
        const items = (data?.items || [])
          .filter((p) => p.logo_url)
          .map((p) => ({
            name: p.short_name || p.org_name,
            sub: p.org_name,
            logo: p.logo_url,
            website: p.website_url || null,
          }));
        setPartners(items.length > 0 ? items : PARTNER_ORGS_FALLBACK.filter((p) => p.logo).slice(0, limit));
      })
      .catch((err) => {
        console.warn('[PartnerStrip] fallback:', err?.message);
        setPartners(PARTNER_ORGS_FALLBACK.filter((p) => p.logo).slice(0, limit));
      });
    return () => { mounted = false; };
  }, [limit]);

  const display = partners.slice(0, limit);
  if (display.length === 0) return null;

  const isLight = variant === 'light';

  return (
    <section
      className={`relative py-12 sm:py-16 px-4 sm:px-6 lg:px-8 border-y ${
        isLight ? 'border-slate-200 bg-slate-50' : 'border-white/5 bg-white/[0.015]'
      }`}
      data-testid="partner-strip"
    >
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-8 sm:mb-10">
          <p className={`text-xs uppercase tracking-[0.2em] font-semibold mb-2 ${isLight ? 'text-violet-700' : 'text-violet-400'}`}>
            Trusted By
          </p>
          <h3 className={`text-2xl sm:text-3xl font-bold ${isLight ? 'text-slate-900' : 'text-white'}`}>
            {heading}
          </h3>
          {subheading && (
            <p className={`mt-2 text-sm sm:text-base ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
              {subheading}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4" data-testid="partner-strip-grid">
          {display.map((org) => (
            <PartnerLogo key={`${org.name}-${org.sub || ''}`} org={org} isLight={isLight} />
          ))}
        </div>

        {showCta && (
          <div className="text-center mt-10">
            <Link
              to="/case-studies"
              className={`inline-flex items-center gap-2 font-semibold text-sm transition-colors ${
                isLight ? 'text-violet-700 hover:text-violet-900' : 'text-cyan-400 hover:text-cyan-300'
              }`}
              data-testid="partner-strip-cta"
            >
              View all case studies <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
